import React, { useState, useEffect, useRef } from "react";
import { Form, Row, Col, Table } from "react-bootstrap";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";
import axios from "axios";
import { toast } from "react-toastify";

const LOCAL_STORAGE_KEY = "inventory_stock_out_items_v1";

export default function StockOut() {
  const { user } = useAuth();

  const [itemCode, setItemCode] = useState("");
  const [itemName, setItemName] = useState("");
  const [quantity, setQuantity] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [items, setItems] = useState([]);

  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  // restore items from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setItems(parsed);
      }
    } catch (e) {
      console.warn("Could not parse stored stock-out items", e);
    }
  }, []);

  // persist items to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.warn("Failed to save stock-out items to localStorage", e);
    }
  }, [items]);

  const isDuplicate = (code) => {
    if (!code) return false;
    return items.some((it) => String(it.itemCode).toLowerCase() === String(code).toLowerCase());
  };

  // debounced lookup for item name
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!itemCode || itemCode.trim() === "") {
      setItemName("");
      setError("");
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      return;
    }

    debounceRef.current = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const fetchItem = async () => {
        setLoading(true);
        setError("");
        setItemName("");

        try {
          const url = `${Base_URL}/Item/GetItemById/id?id=${encodeURIComponent(itemCode.trim())}`;
          const res = await fetch(url, { signal: controller.signal });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();

          setItemName(data?.itemName || "");
          setQuantity(1);
        } catch (err) {
          if (err.name !== "AbortError") setError(err.message || "Failed to fetch item");
        } finally {
          setLoading(false);
        }
      };

      fetchItem();
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [itemCode]);

  const handleAddItem = () => {
    setError("");
    if (!itemCode.trim()) {
      setError("Item code is required to add an item.");
      return;
    }
    if (!quantity || Number(quantity) <= 0) {
      setError("Quantity should be a positive number.");
      return;
    }
    if (isDuplicate(itemCode.trim())) {
      setError("This item code is already added.");
      return;
    }

    const newItem = {
      itemCode: itemCode.trim(),
      itemName: itemName || null,
      quantity: Number(quantity),
    };

    setItems((prev) => [...prev, newItem]);

    setItemCode("");
    setItemName("");
    setQuantity("");
  };

  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveAll = async () => {
    if (items.length === 0) {
      toast.warn("No items to save.");
      return;
    }

    const payload = {
      createdBy: user?.id ?? user?.userId ?? user?.username ?? null,
      createdAt: new Date().toISOString(),
      items: items.map((it) => ({
        itemCode: it.itemCode,
        itemName: it.itemName,
        quantity: it.quantity,
      })),
    };

    try {
        console.log(payload);
      const res = await axios.put(`${Base_URL}/Inventory/UpdateStockOutItem`, payload);
      if (res.status === 200) {
        toast.success("Items removed from inventory (Stock Out).");
        setItems([]);
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      } else {
        toast.error("Error processing stock out.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data || err.message || "Failed to save stock out");
    }
  };

  // scanner Enter handling — prevents form submit / page reload
  const onItemCodeKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // If scanner should auto-add, require qty present >0
      if (quantity && Number(quantity) > 0) {
        if (!isDuplicate(itemCode.trim())) handleAddItem();
        else {
          setError("This item code is already added.");
          setItemCode("");
        }
      } else {
        setError("Please enter quantity (scanner didn't provide it).");
      }
    }
  };

  return (
    <Form className="p-4 bg-white rounded shadow-sm" onSubmit={(e) => e.preventDefault()}>
      <h4 className="mb-4 fw-bold">Stock Out</h4>

      <Row>
        <Col md={4} className="mb-3">
          <Form.Group controlId="itemCode">
            <Form.Label>Item Code</Form.Label>
            <Form.Control
              id="stockOutItemCode"
              type="text"
              value={itemCode}
              onChange={(e) => setItemCode(e.target.value)}
              onKeyDown={onItemCodeKeyDown}
              placeholder="Enter or scan item code"
              autoComplete="off"
              autoFocus
            />
          </Form.Group>
        </Col>

        <Col md={4} className="mb-3">
          <Form.Group controlId="itemName">
            <Form.Label>Item Name</Form.Label>
            <Form.Control
              type="text"
              value={loading ? "Loading..." : itemName}
              disabled
              placeholder="Item name will appear here"
              className="bg-light"
            />
          </Form.Group>
        </Col>

        <Col md={2} className="mb-3">
          <Form.Group controlId="quantity">
            <Form.Label>Quantity</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Qty"
            />
          </Form.Group>
        </Col>

        <Col md={2} className="d-flex align-items-end mb-3">
          <div>
            <button type="button" className="btn btn-outline-primary me-2" onClick={handleAddItem}>
              + Add Item
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSaveAll} disabled={items.length === 0}>
              Save All ({items.length})
            </button>
          </div>
        </Col>
      </Row>

      {error && (
        <Row>
          <Col>
            <small className="text-danger">{error}</small>
          </Col>
        </Row>
      )}

      <Row className="mt-3">
        <Col>
          {items.length === 0 ? (
            <div className="text-muted">No items added yet.</div>
          ) : (
            <Table bordered hover size="sm">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Item Code</th>
                  <th>Item Name</th>
                  <th>Quantity</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{it.itemCode}</td>
                    <td>{it.itemName ?? "-"}</td>
                    <td>{it.quantity}</td>
                    <td>
                      <button className="btn btn-sm btn-danger" onClick={() => handleRemoveItem(idx)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Col>
      </Row>
    </Form>
  );
}
